import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader } from '@kaleyra/emmy';
import cn from 'classnames';
import isEmpty from 'lodash/isEmpty';

import { numberLanguageFormat } from '../../../../utils';
import {
	ChannelType,
	DefaultPropertyType,
	OverviewType,
} from '../../../../interface';

import DataError from '../../templates/Error/DataError';
import NetworkError from '../../templates/Error/NetworkError';

import InfoTable from './InfoTable';
import Channel from './Channel';
import Graph from './Graph';
import Styles from './Widget.module.scss';

export type MapStatePropsType = {
	data: OverviewType[];
	error: any;
	smsInProgress: boolean;
	waInprogress: boolean;
	filters: { [key: string]: any };
	selectedFilter: string;
};

export type DispatchPropsType = {
	onLoad: (filters: { [key: string]: any }) => void;
	onReload: (filters: { [key: string]: any }) => void;
};

export type OwnPropsType = {
	channel: ChannelType;
};

type WidgetPropsType = MapStatePropsType & DispatchPropsType & OwnPropsType;

const Widget: React.FC<WidgetPropsType> = ({
	data,
	error,
	smsInProgress,
	waInprogress,
	channel,
	filters,
	onLoad,
	onReload,
	selectedFilter
}) => {
	const { t } = useTranslation();

	React.useEffect(() => {
		onLoad(filters);
	}, []);

	const getInfo = (channelInfo: ChannelType): React.ReactNode => {
		switch (channelInfo) {
			case ChannelType.sms: {
				const {
					status = [],
					unique_sender: unqSenderId,
					route = [],
				} = data[0];
				const value =  status.filter((item: any) => {
					return item.label !== "Failed"
				}) 
				return (
					<>
						{Boolean(status.length) && (
							<div className={Styles.infoTable}>
								<InfoTable
									data={[
										{
											label: t('overviewWidgetSMSSenderId'),
											value: unqSenderId || 0,
										},
										...value.map<DefaultPropertyType>(
											({ label, percentage }) => ({
												label,
												value: `${numberLanguageFormat(
													percentage
												)}%`,
											})
										),
									]}
								/>
							</div>
						)}
						{Boolean(route.length) && (
							<InfoTable
								data={route
									.filter(({ label }) => label !== 'DEFAULT')
									.map<DefaultPropertyType>(
										({ label, percentage }) => ({
											label,
											value: `${numberLanguageFormat(
												percentage
											)}%`,
										})
									)}
							/>
						)}
					</>
				);
			}
			case ChannelType.whatsApp: {
				const { status = [], overall = [] } = data[0];
				let widgetData: any[] = [];
				const sortOrder = ['Delivered', 'Seen', 'Failed'];
				let sortedWidgetData: any[] = [];
				widgetData.push(
					...status
						.filter(
							({ label }) =>
								!['SENT'].includes(
									label.toUpperCase()
								)
						)
						.map<DefaultPropertyType>(
							({ label, count }) => ({
								label,
								value: count,
							})
						)
				)
				sortOrder.forEach((orderStatus: string) => {
					widgetData.forEach((item: any) => {
						if ((item.label).toLowerCase() === orderStatus.toLowerCase()) {
							sortedWidgetData.push(item);
						}
					})
				});

				return (
					<>
						{Boolean(status.length) && (
							<InfoTable
								data={sortedWidgetData}
							/>
						)}
						{Boolean(overall.length) && (
							<InfoTable
								data={overall
									.filter(({ label }) => label !== 'DEFAULT')
									.map<DefaultPropertyType>(
										({ label, count }) => ({
											label,
											value: count,
										})
									)}
							/>
						)}
					</>
				);
			}
			default:
				return null;
		}
	};

	const checkDataEmpty = (): boolean => {
		const {
			route = [],
			status = [],
			timeVsOverview = [],
			overall = [],
		} = data[0];

		switch (channel) {
			case ChannelType.sms:
				return [route, status, timeVsOverview].every(
					(item) => item.length === 0
				);
			case ChannelType.whatsApp:
				return [overall, status, timeVsOverview].every(
					(item) => item.length === 0
				);
			default:
				return false;
		}
	};

	const getSeries = (): Array<{
		name: string;
		data: Array<number>;
		color: string;
	}> => {
		switch (channel) {
			case ChannelType.sms: {
				const { timeVsOverview = [] } = data[0];
				return [
					{
						name: 'Delivered',
						data: timeVsOverview.map(({ delivered }) => delivered),
						color: '#949CD2',
					},
					{
						name: 'SENT - No DLR',
						data: timeVsOverview.map((item) => item['sent_no_dlr']),
						color: '#D174B7',
					},
					{
						name: 'Failed',
						data: timeVsOverview.map(({ failed }) => failed),
						color: '#9DC9EB',
					},
				];
			}
			case ChannelType.whatsApp: {
				const { timeVsOverview = [] } = data[0];
				return [
					{
						name: 'Incoming',
						data: timeVsOverview.map(({ Incoming }) => Incoming),
						color: '#949CD2',
					},
					{
						name: 'Session',
						data: timeVsOverview.map(({ session }) => session),
						color: '#D174B7',
					},
					{
						name: 'Template',
						data: timeVsOverview.map(({ template }) => template),
						color: '#9DC9EB',
					},
				];
			}
			default:
				return [];
		}
	};

	const getChannelInfo = () => {
		let inProgress = smsInProgress && waInprogress;

		if (typeof inProgress !== 'boolean' || smsInProgress || waInprogress) {
			return (
				<div className={cn('colK', 'colK12', Styles.inProgressContainer)}>
					<Loader marginTop={70} />
				</div>
			);
		} else if (!isEmpty(error) || checkDataEmpty()) {
			let customClassName = '';
			if ((data[0]?.channel_status?.sms && data[0]?.channel_status?.whatsapp) || !isEmpty(error)) {
				customClassName = 'colK6';
			} else {
				customClassName = 'colK12';
			}
			return (
				<div className={cn('colK', Styles.errorContainer, customClassName)}>
					{!isEmpty(error) ? (
						<NetworkError onReload={() => onReload(filters)} />
					) : (
						<DataError />
					)}
				</div>
			);
		} else {
			let customClassName = '';
			if (data[0].channel_status.sms && data[0].channel_status.whatsapp) {
				customClassName = 'colK6';
			} else {
				customClassName = 'colK12';
			}
			return (
				<>
					{data[0].channel_status[channel] &&
						<div className={cn('colK', customClassName, Styles.container)}>
							<p>{t(`channelType${channel}`)}</p>
							<Channel
								graph={
									<Graph
										data={data[0]?.timeVsOverview || []}
										total={data[0]?.total}
										series={getSeries()}
										filter={filters}
										selectedFilter={selectedFilter}
									/>
								}
								info={getInfo(channel)}
								number={data[0].total}
							/>
						</div>
					}
				</>
			);
		}
	};

	return (
		<>
			{getChannelInfo()}
		</>
	);
};

export default Widget;
